import { Button } from '@orbita-ui/core';
import { CentroCostoSelector } from 'core/centrocosto/components/CentroCostoSelector/CentroCostoSelector';
import { CuentaSelector } from 'core/cuenta/components/CuentaSelector/CuentaSelector';
import { TransactionSummaryContext } from 'core/movimientos/contexts/TransactionSummaryContext';
import { ModoConsulta } from 'core/movimientos/models/SearchMovimientosRequest';
import { SearchTotalesMovimientosAgrupadosRequest } from 'core/movimientos/models/SearchTotalesMovimientosAgrupadosRequest';
import { TarjetaSelector } from 'core/tarjeta/components/TarjetaSelector/TarjetaSelector';
import { useContext, useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useForm, useWatch } from 'react-hook-form';
import { FormWithValidation } from 'shared/form';
import { Periodo } from 'shared/models/Periodo';
import { useAuth } from 'auth/hooks/useAuth';
interface FilterTransactionSummaryFormValue {
  periodo: Periodo;
  agruparPor: ModoConsulta;
  numeroCuenta: number;
  centroCostoPadreId: number;
  idTarjeta: number;
}

const AGRUPAR_POR_SELECT_OPTIONS = ['Cuenta', 'Tarjeta'];

const EMPTY_FORM_VALUE: FilterTransactionSummaryFormValue = {
  periodo: null,
  agruparPor: 'CUENTA' as ModoConsulta,
  numeroCuenta: null,
  centroCostoPadreId: null,
  idTarjeta: null,
};

const mapFormData = (
  formData: FilterTransactionSummaryFormValue,
): SearchTotalesMovimientosAgrupadosRequest => {
  return {
    mes: formData.periodo?.mes,
    anio: formData.periodo?.anio,
    agruparPor: formData.agruparPor,
    numeroCuenta: formData.numeroCuenta,
    centroCostoPadreId: formData.centroCostoPadreId,
    idTarjeta: formData.idTarjeta,
  };
};

export const FilterTransactionSummaryForm = () => {
  const { user } = useAuth();

  const { searchParams, searchTransactionSummary, reloadToken } = useContext(
    TransactionSummaryContext,
  );
  const methods = useForm<FilterTransactionSummaryFormValue>({
    defaultValues: EMPTY_FORM_VALUE,
  });

  const isTarjetaUser = user?.tipoUsuario === 'TARJETA';
  const periodo = useWatch({ control: methods.control, name: 'periodo' });
  const agruparPor = useWatch({ control: methods.control, name: 'agruparPor' });
  const centroCostoPadreId = useWatch({
    control: methods.control,
    name: 'centroCostoPadreId',
  });

  useEffect(() => {
    if (searchParams) {
      // reseteamos el formulario con los valores que vienen de la URL
      methods.reset({
        periodo: new Periodo(searchParams.mes, searchParams.anio),
        agruparPor: searchParams.agruparPor,
        numeroCuenta: searchParams.numeroCuenta,
        centroCostoPadreId: searchParams.centroCostoPadreId,
        idTarjeta: searchParams.idTarjeta,
      });
    }
  }, [searchParams, methods]);

  const handleSearch = (data: FilterTransactionSummaryFormValue) => {
    searchTransactionSummary(mapFormData(data));
  };

  return (
    <FormWithValidation
      reactFormHookMethods={methods}
      onSubmit={handleSearch}
      className="m-3"
    >
      <Row>
        <Col>
          <FormWithValidation.LegacySelect.Period
            name="periodo"
            label="Periodo"
            placeholder="Seleccione un período"
          />
        </Col>

        {!isTarjetaUser && (
          <Col>
            <FormWithValidation.LegacySelect
              name="agruparPor"
              label="Agrupar Por"
              options={AGRUPAR_POR_SELECT_OPTIONS}
              mapOptions={true}
            />
          </Col>
        )}
      </Row>

      {!isTarjetaUser && (
        <Row>
          <Col>
            <CentroCostoSelector
              name="centroCostoPadreId"
              label="Depende de"
              reloadToken={reloadToken}
              filtros={{
                cuentasHuerfanas: false,
                periodo,
                centroCostosHojas: agruparPor === 'TARJETA',
              }}
            />
          </Col>

          <Col>
            {agruparPor === 'CUENTA' ? (
              <CuentaSelector
                name="numeroCuenta"
                label="Cuenta"
                filtros={{
                  cuentasHuerfanas: false,
                  periodo,
                  centroCostoOrigenId: centroCostoPadreId,
                }}
                reloadToken={reloadToken}
              />
            ) : (
              <TarjetaSelector
                name="idTarjeta"
                filtros={{
                  cuentasHuerfanas: false,
                  periodo,
                  centroCostoOrigenId: centroCostoPadreId,
                }}
                reloadToken={reloadToken}
              />
            )}
          </Col>
        </Row>
      )}

      <div className="d-flex justify-content-end" style={{ gap: 8 }}>
        <Button
          type="button"
          variant="outline"
          color="destructive"
          onClick={() => methods.reset(EMPTY_FORM_VALUE)}
        >
          Limpiar
        </Button>
        <Button type="submit">Consultar</Button>
      </div>
    </FormWithValidation>
  );
};
